class ContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form')
    this.submitButton = this.querySelector('.contact-form__submit-button')
    this.successMessage = this.querySelector('#success-message')

    this.phonePatterns = {
      '+33': { pattern: '^[0-9]{9}$', placeholder: '6 12 34 56 78' }, // France
      '+32': { pattern: '^[0-9]{8,9}$', placeholder: '4xx xx xx xx' }, // Belgique
      '+41': { pattern: '^[0-9]{9}$', placeholder: '7x xxx xx xx' }, // Suisse
      '+352': { pattern: '^[0-9]{8,11}$', placeholder: 'xxx xxx xxx' }, // Luxembourg
      '+1': { pattern: '^[0-9]{10}$', placeholder: '555 123 4567' }, // US
      '+44': { pattern: '^[0-9]{10}$', placeholder: '7xxx xxxxxx' }, // UK
      '+49': { pattern: '^[0-9]{10,11}$', placeholder: 'xxx xxxxxxxx' }, // Allemagne
      '+34': { pattern: '^[0-9]{9}$', placeholder: '6xx xx xx xx' }, // Espagne
      '+39': { pattern: '^[0-9]{9,10}$', placeholder: '3xx xxx xxxx' }, // Italie
      '+971': { pattern: '^[0-9]{9}$', placeholder: '5x xxx xxxx' }, // UAE
    }

    this.checkSuccessMessage()
    this.setupCharacterCounter()
    this.setupCheckboxGroups()

    // Wait for blocks to be fully rendered before setting up
    requestAnimationFrame(() => {
      // Restructure DOM for desktop columns first
      this.setupDestinationsGrid()
      // Then setup dropdown (after DOM is stable)
      this.setupDestinationsDropdown()
    })

    this.setupPhoneValidation()
    this.setupSelectRotation()
    this.setupFormValidation()
    this.setupFormSubmission()

    if (
      this.dataset.retractationEndpoint &&
      this.form instanceof HTMLFormElement
    ) {
      this.form.setAttribute('action', '#')
    }
  }

  checkSuccessMessage() {
    const urlParams = new URLSearchParams(window.location.search)
    const contactPosted = urlParams.get('contact_posted')

    if (contactPosted === 'true') {
      this.showSuccess()
    }
  }

  /**
   * Hides the form and displays the success message.
   */
  showSuccess() {
    if (!this.successMessage) return

    this.successMessage.style.display = 'block'
    this.hideRetractationError()

    const formHeader = this.querySelector('.contact-form__header')
    if (formHeader) {
      formHeader.style.display = 'none'
    }

    const formFields = this.querySelector('.contact-form__fields')
    if (formFields) {
      formFields.style.display = 'none'
    }

    setTimeout(() => {
      this.successMessage.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }, 100)
  }

  /**
   * @param {string} fieldName
   * @returns {string}
   */
  getContactFieldValue(fieldName) {
    const field = this.form.querySelector(`[name="contact[${fieldName}]"]`)
    if (
      !(field instanceof HTMLInputElement) &&
      !(field instanceof HTMLSelectElement) &&
      !(field instanceof HTMLTextAreaElement)
    ) {
      return ''
    }

    return field.value.trim()
  }

  /**
   * @returns {HTMLElement|null}
   */
  getRetractationError() {
    const error = this.querySelector('#retractation-error')
    return error instanceof HTMLElement ? error : null
  }

  hideRetractationError() {
    const error = this.getRetractationError()
    if (error) {
      error.hidden = true
    }
  }

  /**
   * @param {'submit' | 'deadline' | 'reception_future' | 'reception_before_order'} [kind]
   */
  showRetractationError(kind = 'submit') {
    const error = this.getRetractationError()
    if (!error) return

    /** @type {Record<string, string | undefined>} */
    const messages = {
      submit: error.dataset.errorSubmit,
      deadline: error.dataset.errorDeadline,
      reception_future: error.dataset.errorReceptionFuture,
      reception_before_order: error.dataset.errorReceptionBeforeOrder,
    }
    const message = messages[kind] ?? error.dataset.errorSubmit
    const messageNode = error.querySelector('p')
    if (messageNode instanceof HTMLElement && message) {
      messageNode.textContent = message
    }

    error.hidden = false
    error.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  /**
   * Parses a YYYY-MM-DD value into a local calendar date.
   * @param {string} dateValue
   * @returns {Date | null}
   */
  parseLocalDate(dateValue) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue)
    if (!match) return null

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(year, month - 1, day)

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null
    }

    return date
  }

  /**
   * Returns whole calendar days between the order date and today.
   * @param {string} orderDateValue
   * @returns {number | null}
   */
  getDaysSinceOrderDate(orderDateValue) {
    const orderDate = this.parseLocalDate(orderDateValue)
    if (!orderDate) return null

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const msPerDay = 24 * 60 * 60 * 1000

    return Math.round((today.getTime() - orderDate.getTime()) / msPerDay)
  }

  /**
   * Withdrawal is allowed only when the order date is 14 days ago or less.
   * @param {string} orderDateValue
   * @returns {boolean}
   */
  isRetractationDeadlineExpired(orderDateValue) {
    const daysSinceOrder = this.getDaysSinceOrderDate(orderDateValue)
    if (daysSinceOrder == null) return true

    return daysSinceOrder > 14
  }

  /**
   * Validates reception date against today and the order date.
   * @param {string} orderDateValue
   * @param {string} receptionDateValue
   * @returns {'reception_future' | 'reception_before_order' | null}
   */
  getReceptionDateErrorKind(orderDateValue, receptionDateValue) {
    const receptionDate = this.parseLocalDate(receptionDateValue)
    if (!receptionDate) return null

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    if (receptionDate.getTime() > today.getTime()) {
      return 'reception_future'
    }

    const orderDate = this.parseLocalDate(orderDateValue)
    if (orderDate && receptionDate.getTime() < orderDate.getTime()) {
      return 'reception_before_order'
    }

    return null
  }

  /**
   * Posts the retractation payload to the app proxy and shows success or error.
   * @returns {Promise<void>}
   */
  async submitRetractation() {
    const endpoint = this.dataset.retractationEndpoint
    if (!endpoint || !this.form) return

    const payload = {
      last_name: this.getContactFieldValue('last_name'),
      first_name: this.getContactFieldValue('first_name'),
      email: this.getContactFieldValue('email'),
      order_number: this.getContactFieldValue('order_number'),
      order_date: this.getContactFieldValue('order_date'),
      reception_date: this.getContactFieldValue('reception_date'),
      product_type: this.getContactFieldValue('product_type'),
      product_title: this.getContactFieldValue('product_title'),
    }

    this.hideRetractationError()

    const receptionDateError = this.getReceptionDateErrorKind(
      payload.order_date,
      payload.reception_date
    )
    if (receptionDateError) {
      this.showRetractationError(receptionDateError)
      return
    }

    if (this.isRetractationDeadlineExpired(payload.order_date)) {
      this.showRetractationError('deadline')
      return
    }

    if (this.submitButton) {
      this.submitButton.disabled = true
    }

    let didSucceed = false

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.status === 422) {
        this.showRetractationError('deadline')
        return
      }

      if (!response.ok) {
        this.showRetractationError('submit')
        return
      }

      didSucceed = true
      this.showSuccess()
    } catch {
      this.showRetractationError('submit')
    } finally {
      if (this.submitButton && !didSucceed) {
        this.submitButton.disabled = false
      }
    }
  }

  setupCharacterCounter() {
    const textarea = this.querySelector('#details')
    const charCount = this.querySelector('.char-count')

    if (textarea && charCount) {
      textarea.addEventListener('input', () => {
        const count = textarea.value.length
        charCount.textContent = String(count).padStart(3, '0')

        // Change color when approaching limit
        if (count > 400) {
          charCount.style.color = '#c00'
        } else if (count > 350) {
          charCount.style.color = '#ff9800'
        } else {
          charCount.style.color = ''
        }
      })
    }
  }

  setupCheckboxGroups() {
    const checkboxGroups = this.querySelectorAll('[data-checkbox-group]')

    checkboxGroups.forEach((group) => {
      const hiddenInput = group.querySelector('[data-checkbox-hidden]')
      const checkboxes = group.querySelectorAll('.contact-form__checkbox-input')

      if (!hiddenInput || !checkboxes.length) return

      const updateHiddenInput = () => {
        const checkedValues = Array.from(checkboxes)
          .filter((cb) => cb.checked)
          .map((cb) => cb.getAttribute('data-checkbox-value'))
          .join(', ')

        hiddenInput.value = checkedValues
      }

      // Update on change
      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', updateHiddenInput)
      })

      // Initialize
      updateHiddenInput()
    })
  }

  setupSelectRotation() {
    // Handle rotation for pays select
    const paysSelect = this.querySelector('#pays')
    if (paysSelect) {
      let checkInterval = null

      const openSelect = () => {
        paysSelect.classList.add('is-open')

        // Start checking if select is still focused
        if (checkInterval) clearInterval(checkInterval)
        checkInterval = setInterval(() => {
          if (document.activeElement !== paysSelect) {
            closeSelect()
          }
        }, 100)
      }

      const closeSelect = () => {
        paysSelect.classList.remove('is-open')
        if (checkInterval) {
          clearInterval(checkInterval)
          checkInterval = null
        }
      }

      // Mobile and desktop support
      paysSelect.addEventListener('touchstart', openSelect)
      paysSelect.addEventListener('mousedown', openSelect)
      paysSelect.addEventListener('focus', openSelect)
      paysSelect.addEventListener('blur', closeSelect)
      paysSelect.addEventListener('change', closeSelect)
    }

    // Handle rotation for phone country select
    const countrySelect = this.querySelector('#telephone-country')
    const countryWrapper = this.querySelector('.input-phone__country-wrapper')
    const flagDisplay = this.querySelector('.input-phone__flag-display')

    if (countrySelect && countryWrapper) {
      let checkInterval = null

      const updateFlagDisplay = () => {
        const selectedOption =
          countrySelect.options[countrySelect.selectedIndex]
        const flagUrl = selectedOption.getAttribute('data-flag-url')
        const countryCode = selectedOption.getAttribute('data-country')
        if (flagUrl && flagDisplay) {
          flagDisplay.src = flagUrl
          flagDisplay.alt = countryCode
        }
      }

      const openSelect = () => {
        countrySelect.classList.add('is-open')
        countryWrapper.classList.add('is-open')

        // Start checking if select is still focused
        if (checkInterval) clearInterval(checkInterval)
        checkInterval = setInterval(() => {
          if (document.activeElement !== countrySelect) {
            closeSelect()
          }
        }, 100)
      }

      const closeSelect = () => {
        countrySelect.classList.remove('is-open')
        countryWrapper.classList.remove('is-open')
        if (checkInterval) {
          clearInterval(checkInterval)
          checkInterval = null
        }
      }

      // Mobile and desktop support
      countrySelect.addEventListener('touchstart', openSelect)
      countrySelect.addEventListener('mousedown', openSelect)
      countrySelect.addEventListener('focus', openSelect)
      countrySelect.addEventListener('blur', closeSelect)

      countrySelect.addEventListener('change', () => {
        closeSelect()
        updateFlagDisplay()
      })

      // Initialize flag display
      updateFlagDisplay()
    }
  }

  setupDestinationsGrid() {
    const panel = this.querySelector('.contact-form__destinations-panel')
    if (!panel) return

    const destinationGroups = this.querySelectorAll(
      '.contact-form__destination-group'
    )

    if (destinationGroups.length === 0) return

    // Helper to check if we're on desktop
    const isDesktop = () => window.innerWidth >= 1024

    // Only restructure on desktop
    if (!isDesktop()) {
      return
    }

    // Collect all groups with their column and order (including their shopify-block wrapper)
    const groups = Array.from(destinationGroups).map((group) => {
      const wrapper = group.parentElement
      return {
        element: group,
        wrapper:
          wrapper && wrapper.classList.contains('shopify-block')
            ? wrapper
            : null,
        column: parseInt(group.dataset.column) || 1,
        order: parseInt(group.dataset.order) || 0,
      }
    })

    // Sort by column first, then by order within each column
    groups.sort((a, b) => {
      if (a.column !== b.column) return a.column - b.column
      return a.order - b.order
    })

    // Find max column number
    const maxColumn = Math.max(...groups.map((g) => g.column))

    // Create column containers
    const columnsContainer = document.createElement('div')
    columnsContainer.className = 'contact-form__destinations-columns'

    const columnWrappers = []
    for (let i = 1; i <= maxColumn; i++) {
      const columnWrapper = document.createElement('div')
      columnWrapper.className = 'contact-form__destination-column'
      columnWrapper.dataset.column = i
      columnsContainer.appendChild(columnWrapper)
      columnWrappers[i] = columnWrapper
    }

    // Move groups into their respective columns
    groups.forEach((group) => {
      const targetColumn = columnWrappers[group.column]
      if (targetColumn && group.wrapper) {
        targetColumn.appendChild(group.wrapper)
      }
    })

    // Replace the panel's content with the new structure
    // Keep the header, add the columns after it
    const header = panel.querySelector('.contact-form__destinations-header')

    // Remove all shopify-block elements (they're now in columnsContainer)
    panel.querySelectorAll('.shopify-block').forEach((block) => block.remove())

    // Add the columns container
    panel.appendChild(columnsContainer)
  }

  setupPhoneValidation() {
    const countryCodeSelect = this.querySelector('#telephone-country')
    const phoneInput = this.querySelector('#telephone')

    if (countryCodeSelect && phoneInput) {
      const updatePhoneValidation = () => {
        const countryCode = countryCodeSelect.value
        const config = this.phonePatterns[countryCode]

        if (config) {
          phoneInput.pattern = config.pattern
          phoneInput.placeholder = config.placeholder
        }
      }

      // Set initial pattern and placeholder
      updatePhoneValidation()

      // Update pattern and placeholder when country changes
      countryCodeSelect.addEventListener('change', updatePhoneValidation)
    }
  }

  setupDestinationsDropdown() {
    const trigger = this.querySelector('.contact-form__destinations-trigger')
    const panel = this.querySelector('.contact-form__destinations-panel')
    const triggerText = this.querySelector(
      '.contact-form__destinations-trigger-text'
    )
    const triggerIcon = this.querySelector(
      '.contact-form__destinations-trigger-icon'
    )
    const globalSelectAll = this.querySelector('.select-all-global')
    const allDestinations = this.querySelectorAll('.destination-checkbox')
    const regionHeaders = this.querySelectorAll(
      '.contact-form__destination-header'
    )
    const regionSelectAlls = this.querySelectorAll('.select-all-region')

    if (!trigger || !panel) return

    // Helper to check if we're on mobile
    const isMobile = () => window.innerWidth < 1024

    // Toggle dropdown (only on mobile)
    trigger.addEventListener('click', () => {
      if (!isMobile()) return
      const isOpen = panel.classList.toggle('is-open')
      triggerIcon.classList.toggle('is-open', isOpen)
    })

    // Close dropdown when clicking outside (only on mobile)
    document.addEventListener('click', (e) => {
      if (!isMobile()) return
      if (!this.contains(e.target)) {
        panel.classList.remove('is-open')
        triggerIcon.classList.remove('is-open')
      }
    })

    // Toggle region collapse (only on mobile)
    regionHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        if (!isMobile()) return
        const isExpanded = header.getAttribute('aria-expanded') === 'true'
        header.classList.toggle('is-open')
        header.setAttribute('aria-expanded', !isExpanded)
      })
    })

    // Update trigger text when selections change
    const updateTriggerText = () => {
      const checkedCount = Array.from(allDestinations).filter(
        (cb) => cb.checked
      ).length

      if (checkedCount === 0) {
        triggerText.textContent = triggerText.dataset.placeholder
        triggerText.classList.remove('has-selection')
      } else if (checkedCount === allDestinations.length) {
        triggerText.textContent = 'Tout sélectionner'
        triggerText.classList.add('has-selection')
      } else {
        triggerText.textContent = `${checkedCount} destination${checkedCount > 1 ? 's' : ''} sélectionnée${checkedCount > 1 ? 's' : ''}`
        triggerText.classList.add('has-selection')
      }
    }

    // Update global select all state
    const updateGlobalSelectAll = () => {
      if (globalSelectAll && allDestinations.length > 0) {
        const allChecked = Array.from(allDestinations).every((cb) => cb.checked)
        const someChecked = Array.from(allDestinations).some((cb) => cb.checked)

        globalSelectAll.checked = allChecked
        globalSelectAll.indeterminate = someChecked && !allChecked
      }
    }

    // Global select all
    if (globalSelectAll) {
      globalSelectAll.addEventListener('change', (e) => {
        const isChecked = e.target.checked
        allDestinations.forEach((cb) => (cb.checked = isChecked))
        // Also update all region select-all checkboxes
        regionSelectAlls.forEach((selectAll) => {
          selectAll.checked = isChecked
          selectAll.indeterminate = false
        })
        updateTriggerText()
      })
    }

    // Region select all functionality
    regionSelectAlls.forEach((selectAll) => {
      const regionId = selectAll.dataset.region
      const regionCheckboxes = this.querySelectorAll(
        `.destination-checkbox[data-region="${regionId}"]`
      )

      // When region select all is clicked
      selectAll.addEventListener('change', (e) => {
        const isChecked = e.target.checked
        regionCheckboxes.forEach((cb) => (cb.checked = isChecked))
        updateGlobalSelectAll()
        updateTriggerText()
      })

      // Update region select all when individual checkboxes in that region change
      regionCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const allChecked = Array.from(regionCheckboxes).every(
            (cb) => cb.checked
          )
          const someChecked = Array.from(regionCheckboxes).some(
            (cb) => cb.checked
          )

          selectAll.checked = allChecked
          selectAll.indeterminate = someChecked && !allChecked
        })
      })

      // Initial state for this region
      const allChecked = Array.from(regionCheckboxes).every((cb) => cb.checked)
      const someChecked = Array.from(regionCheckboxes).some((cb) => cb.checked)
      selectAll.checked = allChecked
      selectAll.indeterminate = someChecked && !allChecked
    })

    // Update global select all when individual checkboxes change
    allDestinations.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        updateGlobalSelectAll()
        updateTriggerText()
      })
    })

    // Initial state
    updateTriggerText()
    updateGlobalSelectAll()
  }

  setupFormValidation() {
    const requiredFields = this.querySelectorAll('[required]')

    const validateForm = () => {
      let isValid = true

      requiredFields.forEach((field) => {
        if (field.type === 'radio') {
          const radioGroup = this.querySelectorAll(`[name="${field.name}"]`)
          const isGroupChecked = Array.from(radioGroup).some(
            (radio) => radio.checked
          )
          if (!isGroupChecked) isValid = false
        } else if (field.type === 'checkbox') {
          if (!field.checked) isValid = false
        } else if (field.type === 'email') {
          const emailValid = field.validity.valid && field.value.trim() !== ''
          if (!emailValid) isValid = false
        } else {
          if (field.value.trim() === '') isValid = false
        }
      })

      this.submitButton.disabled = !isValid
    }

    // Show validation error on blur for contact fields
    const contactFields = this.querySelectorAll(
      '.contact-form__contact-section .contact-form__input, .contact-form__contact-section .contact-form__select, .contact-form__contact-section .input-phone__number'
    )
    contactFields.forEach((field) => {
      field.addEventListener('blur', () => {
        const fieldWrapper = field.closest('.contact-form__field')
        if (field.validity.valid) {
          fieldWrapper.classList.remove('show-error')
        } else if (field.value.trim() !== '' || field.required) {
          fieldWrapper.classList.add('show-error')
        }
      })

      // Remove error state when user starts typing
      field.addEventListener('input', () => {
        const fieldWrapper = field.closest('.contact-form__field')
        if (field.validity.valid) {
          fieldWrapper.classList.remove('show-error')
        }
      })
    })

    // Validate on input/change
    this.form.addEventListener('input', validateForm)
    this.form.addEventListener('change', validateForm)

    // Initial validation
    validateForm()
  }

  setupFormSubmission() {
    this.form.addEventListener('submit', async (e) => {
      const isRetractation = Boolean(this.dataset.retractationEndpoint)
      if (isRetractation) {
        e.preventDefault()
      }

      // Check if form is valid
      const isValid = this.form.checkValidity()

      if (!isValid) {
        // Prevent submission only if validation fails
        e.preventDefault()

        // Show error state on all invalid fields
        const contactFields = this.querySelectorAll(
          '.contact-form__contact-section .contact-form__input, .contact-form__contact-section .contact-form__select, .contact-form__contact-section .input-phone__number'
        )
        contactFields.forEach((field) => {
          const fieldWrapper = field.closest('.contact-form__field')
          if (!field.validity.valid) {
            fieldWrapper.classList.add('show-error')
          }
        })

        // Scroll to first invalid field
        const firstInvalid = this.querySelector(
          '.contact-form__field.show-error'
        )
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return
      }

      if (isRetractation) {
        await this.submitRetractation()
        return
      }

      const destinationCheckboxes = this.querySelectorAll(
        '.destination-checkbox:checked'
      )
      if (destinationCheckboxes.length > 0) {
        // Collect all checked values
        const destinationValues = Array.from(destinationCheckboxes).map(
          (cb) => cb.value
        )
        const destinationsString = destinationValues.join(', ')

        // Remove any existing hidden destinations field
        const existingHidden = this.form.querySelector(
          'input[name="contact[destinations]"]'
        )
        if (existingHidden) {
          existingHidden.remove()
        }

        // Create a hidden input with the concatenated values
        const hiddenInput = document.createElement('input')
        hiddenInput.type = 'hidden'
        hiddenInput.name = 'contact[destinations]'
        hiddenInput.value = destinationsString
        this.form.appendChild(hiddenInput)

        // Disable all destination checkboxes so they're not submitted
        destinationCheckboxes.forEach((cb) => {
          cb.disabled = true
        })
      }

      // If valid, allow the form to submit naturally to Shopify
      // Shopify will handle the submission and redirect
    })
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactForm)
}
